import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadControl from 'components/formfields/ImageUploadControl';
import { ApplicableCoursePayload, LimitType } from 'features/CourseOrderLimit/models/courseOrderLimit';
import { courseOrderLimitService } from 'features/CourseOrderLimit/services';
import {
  COURSE_CLASSIFICATION_ALL_YOU_CAN_DRINK,
  COURSE_CLASSIFICATION_ALL_YOU_CAN_EAT,
} from 'features/MenuBook/constants';
import courseService from 'features/MenuBook/courseService';
import menuBookService from 'features/MenuBook/menuBookService';
import { Course, MenuCourse } from 'features/MenuBook/models/course';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { CouponClassificationMap } from 'features/common/models';
import { CourseClassification, CourseClassificationMap } from 'features/common/models/courseClassification';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IMAGE_SIZES, displayError } from 'utils';
import { handleServiceError } from 'utils/axiosError';
import * as yup from 'yup';
import SelectCourseMenuModal from '../SelectCourseMenuModal';
import './style.scss';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TEXT_COLOR = '#000000';

type FormValues = {
  name: string;
  classification: number;
  priority: number;
  time_limit_minutes: number;
  limit_type: number;

  menu_ids: number[];
  menu_additional_ids: number[];

  display_name_ja: string;
  display_name_en: string;
  display_name_vi: string;
  display_name_ko: string;
  display_name_zh_hant: string;
  display_name_zh_hans: string;

  note: string;
  menu_book_id: number;
  copyFromCourseId?: number;

  mobile_order_img_ja: string | null;
  mobile_order_img_en: string | null;
  mobile_order_img_vi: string | null;
  mobile_order_img_ko: string | null;
  mobile_order_img_zh_hant: string | null;
  mobile_order_img_zh_hans: string | null;

  tablet_order_img_ja: string | null;
  tablet_order_img_en: string | null;
  tablet_order_img_vi: string | null;
  tablet_order_img_ko: string | null;
  tablet_order_img_zh_hant: string | null;
  tablet_order_img_zh_hans: string | null;

  background_color: string;
  text_color: string;
  background_flg: boolean;
  introduce_flg: boolean;

  background_img_ja: string | null;
  background_img_en: string | null;
  background_img_vi: string | null;
  background_img_ko: string | null;
  background_img_zh_hant: string | null;
  background_img_zh_hans: string | null;
};

export interface CourseDetailModalProps {
  menuBookId: number;
  menuBookName: string;
  courseId?: number;
  copyFromCourseId?: number;
  onClose?: (isDataChanged: boolean) => void;
}

function CourseDetailModal({ menuBookId, menuBookName, courseId, copyFromCourseId, onClose }: CourseDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const DEFAULT_LIMIT_TYPE = { id: 0, name: t('CourseDetailModal.no_limit_type') };

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [isLoading, setIsLoading] = useState(true);
  const [showSelectTargetMenu, setShowSelectTargetMenu] = useState(false);
  const [showSelectConditionMenu, setShowSelectConditionMenu] = useState(false);
  const [classificationSource, setClassificationSource] = useState<CourseClassificationMap>();
  const [couponClassificationSource, setCouponClassificationSource] = useState<CouponClassificationMap>();
  const [courseData, setCourseData] = useState<Course>();
  const [menuCourses, setMenuCourses] = useState<MenuCourse[]>([]);
  const [menuCourseAdditionals, setMenuCourseAdditionals] = useState<MenuCourse[]>([]);
  const [limitTypeList, setLimitTypeList] = useState<LimitType[]>([]);
  const [isLimitTypeListReady, setIsLimitTypeListReady] = useState(false);
  const [autoAddMenuCourses, setAutoAddMenuCourses] = useState<MenuCourse[]>([]);

  // ref
  const backgroundSettingInputRef = useRef<HTMLInputElement>(null);
  const colorSettingInputRef = useRef<HTMLInputElement>(null);

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('CourseDetailModal.warn_field_required')),
    priority: yup
      .number()
      .min(0, t('CourseDetailModal.warn_value_number_greater_equal_0'))
      .integer(t('CourseDetailModal.warn_value_number_integer'))
      .transform((v, o) => (typeof o === 'string' && o.trim() === '' ? undefined : v))
      .typeError(t('CourseDetailModal.warn_field_required'))
      .required(t('CourseDetailModal.warn_field_required')),
  });

  // form setup
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      classification: 1,
      priority: 1,
      time_limit_minutes: 0,
      limit_type: 0,
      menu_ids: [],
      menu_additional_ids: [],

      display_name_ja: '',
      display_name_en: '',
      display_name_vi: '',
      display_name_ko: '',
      display_name_zh_hant: '',
      display_name_zh_hans: '',

      note: '',
      menu_book_id: menuBookId,

      mobile_order_img_ja: '',
      mobile_order_img_en: '',
      mobile_order_img_vi: '',
      mobile_order_img_ko: '',
      mobile_order_img_zh_hant: '',
      mobile_order_img_zh_hans: '',

      tablet_order_img_ja: '',
      tablet_order_img_en: '',
      tablet_order_img_vi: '',
      tablet_order_img_ko: '',
      tablet_order_img_zh_hant: '',
      tablet_order_img_zh_hans: '',

      background_color: DEFAULT_BACKGROUND_COLOR,
      text_color: DEFAULT_TEXT_COLOR,
      background_flg: false,
      introduce_flg: true,

      background_img_ja: '',
      background_img_en: '',
      background_img_vi: '',
      background_img_ko: '',
      background_img_zh_hant: '',
      background_img_zh_hans: '',
    },
    mode: 'onSubmit',
  });

  const watchName = useWatch({ control, name: 'name', defaultValue: '' });
  const watchBackgroundColor = useWatch({
    control,
    name: 'background_color',
    defaultValue: getValues('background_color'),
  });
  const watchTextColor = useWatch({ control, name: 'text_color', defaultValue: getValues('text_color') });
  // const watchBackgroundFlg = useWatch({ control, name: 'background_flg', defaultValue: getValues('background_flg') });
  const watchBackgroundFlg = false;
  const watchIntroduceFlg = useWatch({ control, name: 'introduce_flg', defaultValue: getValues('introduce_flg') });

  useEffect(() => {
    // load data limit type
    (async () => {
      try {
        // List limit type
        const res = await masterDataService.getCourseOrderLimitType();
        const list = [DEFAULT_LIMIT_TYPE, ...res.data['limit_type']];
        setLimitTypeList(list);
        setIsLimitTypeListReady(true);
      } catch (error) {
        if (!handleServiceError(showGlobalModal, error as AxiosError)) {
          displayError(showGlobalModal, error as AxiosError);
        }
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAutoAddMenuCourses();
    loadDataClassification();
    loadDataCouponClassification();
    loadDataCourseFromCourseId();
    loadDataCourseFromCopy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitTypeList]);

  useEffect(() => {
    if (courseId) {
      if (classificationSource && courseData) {
        setIsLoading(false);
      }
    } else {
      if (classificationSource) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationSource, courseData]);

  function loadAutoAddMenuCourses() {
    //load data target_menus
    (async () => {
      try {
        const response = await menuBookService.getTargetMenus(menuBookId);
        setAutoAddMenuCourses(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadDataLimitType(targetCourseId: number) {
    (async () => {
      try {
        if (!isLimitTypeListReady) return;
        // Limit type of course
        const response = await courseOrderLimitService.getCourseOrderLimitList({ courseId: targetCourseId });
        const data = response.data[0];
        if (data) {
          const initLimitType = limitTypeList.find((item: LimitType) => item.id === data.limit_type);
          if (initLimitType) {
            setValue('limit_type', Number(initLimitType.id));
          }
        }
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadDataClassification() {
    try {
      // load data classification
      (async () => {
        const response = await masterDataService.getCourseClassification();
        const classificationSourceMap = response.data.course_classification.reduce(
          (classificationSourceMap: CourseClassificationMap, classification: CourseClassification) => {
            classificationSourceMap[classification.id] = classification.name;
            return classificationSourceMap;
          },
          {}
        );
        setClassificationSource(classificationSourceMap);
      })();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function loadDataCouponClassification() {
    //load data coupon_classification
    (async () => {
      try {
        const response = await masterDataService.getCouponClassification();
        const couponClassificationMap = response.data.coupon_classification.reduce(
          (couponClassificationMap: CouponClassificationMap, coupon) => {
            couponClassificationMap[coupon.id] = coupon.name;
            return couponClassificationMap;
          },
          {}
        );
        setCouponClassificationSource(couponClassificationMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadDataCourseFromCourseId() {
    //load data course follow courseId
    if (courseId) {
      loadDataLimitType(courseId);

      try {
        (async () => {
          const response = await courseService.getItem(courseId);
          setCourseData(response.data);
          setMenuCourses(response.data.menu_courses);
          setMenuCourseAdditionals(response.data.menu_course_additionals);

          setValue('name', response.data.name);
          setValue('classification', response.data.classification);
          setValue('priority', response.data.priority);
          setValue('time_limit_minutes', response.data.time_limit_minutes);
          setValue('display_name_ja', response.data.display_name_ja);
          setValue('display_name_en', response.data.display_name_en);
          setValue('display_name_vi', response.data.display_name_vi);
          setValue('display_name_ko', response.data.display_name_ko);
          setValue('display_name_zh_hant', response.data.display_name_zh_hant);
          setValue('display_name_zh_hans', response.data.display_name_zh_hans);
          setValue('note', response.data.note);

          setValue('background_color', response.data.background_color);
          setValue('text_color', response.data.text_color);
          setValue('background_flg', response.data.background_flg);
          setValue('introduce_flg', response.data.introduce_flg);

          setValue('background_img_ja', response.data.background_img_ja);
          setValue('background_img_en', response.data.background_img_en);
          setValue('background_img_vi', response.data.background_img_vi);
          setValue('background_img_ko', response.data.background_img_ko);
          setValue('background_img_zh_hant', response.data.background_img_zh_hant);
          setValue('background_img_zh_hans', response.data.background_img_zh_hans);

          setValue('mobile_order_img_ja', response.data.mobile_order_img_ja);
          setValue('mobile_order_img_en', response.data.mobile_order_img_en);
          setValue('mobile_order_img_vi', response.data.mobile_order_img_vi);
          setValue('mobile_order_img_ko', response.data.mobile_order_img_ko);
          setValue('mobile_order_img_zh_hant', response.data.mobile_order_img_zh_hant);
          setValue('mobile_order_img_zh_hans', response.data.mobile_order_img_zh_hans);

          setValue('tablet_order_img_ja', response.data.tablet_order_img_ja);
          setValue('tablet_order_img_en', response.data.tablet_order_img_en);
          setValue('tablet_order_img_vi', response.data.tablet_order_img_vi);
          setValue('tablet_order_img_ko', response.data.tablet_order_img_ko);
          setValue('tablet_order_img_zh_hant', response.data.tablet_order_img_zh_hant);
          setValue('tablet_order_img_zh_hans', response.data.tablet_order_img_zh_hans);

          const menu_ids = response.data.menu_courses.map((menu) => menu.menu_id);
          const menu_addiotional_ids = response.data.menu_course_additionals.map((menu) => menu.menu_id);
          setValue('menu_ids', menu_ids);
          setValue('menu_additional_ids', menu_addiotional_ids);
        })();
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    }
  }

  function loadDataCourseFromCopy() {
    //load data course follow copyFromCourseId
    if (copyFromCourseId) {
      loadDataLimitType(copyFromCourseId);

      try {
        (async () => {
          const response = await courseService.getItem(copyFromCourseId);
          setCourseData(response.data);
          setMenuCourses(response.data.menu_courses);
          setMenuCourseAdditionals(response.data.menu_course_additionals);

          setValue('name', response.data.name);
          setValue('classification', response.data.classification);
          setValue('priority', response.data.priority);
          setValue('time_limit_minutes', response.data.time_limit_minutes);
          setValue('display_name_ja', response.data.display_name_ja);
          setValue('display_name_en', response.data.display_name_en);
          setValue('display_name_vi', response.data.display_name_vi);
          setValue('display_name_ko', response.data.display_name_ko);
          setValue('display_name_zh_hant', response.data.display_name_zh_hant);
          setValue('display_name_zh_hans', response.data.display_name_zh_hans);
          setValue('note', response.data.note);

          setValue('background_color', response.data.background_color);
          setValue('text_color', response.data.text_color);
          setValue('background_flg', response.data.background_flg);
          setValue('introduce_flg', response.data.introduce_flg);

          setValue('background_img_ja', response.data.background_img_ja);
          setValue('background_img_en', response.data.background_img_en);
          setValue('background_img_vi', response.data.background_img_vi);
          setValue('background_img_ko', response.data.background_img_ko);
          setValue('background_img_zh_hant', response.data.background_img_zh_hant);
          setValue('background_img_zh_hans', response.data.background_img_zh_hans);

          setValue('mobile_order_img_ja', response.data.mobile_order_img_ja);
          setValue('mobile_order_img_en', response.data.mobile_order_img_en);
          setValue('mobile_order_img_vi', response.data.mobile_order_img_vi);
          setValue('mobile_order_img_ko', response.data.mobile_order_img_ko);
          setValue('mobile_order_img_zh_hant', response.data.mobile_order_img_zh_hant);
          setValue('mobile_order_img_zh_hans', response.data.mobile_order_img_zh_hans);

          setValue('tablet_order_img_ja', response.data.tablet_order_img_ja);
          setValue('tablet_order_img_en', response.data.tablet_order_img_en);
          setValue('tablet_order_img_vi', response.data.tablet_order_img_vi);
          setValue('tablet_order_img_ko', response.data.tablet_order_img_ko);
          setValue('tablet_order_img_zh_hant', response.data.tablet_order_img_zh_hant);
          setValue('tablet_order_img_zh_hans', response.data.tablet_order_img_zh_hans);

          const menu_ids = response.data.menu_courses.map((menu) => menu.menu_id);
          const menu_addiotional_ids = response.data.menu_course_additionals.map((menu) => menu.menu_id);
          setValue('menu_ids', menu_ids);
          setValue('menu_additional_ids', menu_addiotional_ids);
          setValue('copyFromCourseId', copyFromCourseId);
        })();
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    }
  }

  function handleClose(isDataChanged: boolean = false) {
    onClose && onClose(isDataChanged);
  }

  function handleBackgroundSettingClick() {
    if (backgroundSettingInputRef.current) {
      backgroundSettingInputRef.current.click();
    }
  }

  function handleColorSettingClick() {
    if (colorSettingInputRef.current) {
      colorSettingInputRef.current.click();
    }
  }

  function onSubmit(data: FormValues) {
    if (courseId) {
      //update data
      (async () => {
        try {
          const response = await courseService.update(courseId, data);
          // update limit type
          if (limitTypeList) {
            const selectedItem = Number(data.limit_type);

            await updateLimitType(selectedItem, courseId);
          }
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    } else {
      // add data
      (async () => {
        try {
          const response = await courseService.add(data);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }
  async function updateLimitType(limitType: number, courseId: number) {
    if (limitType > 0) {
      const payload: ApplicableCoursePayload[] = [
        {
          course_id: courseId,
          limit_type: Number(limitType),
        },
      ];
      await courseOrderLimitService.addCourseOrderLimit(payload);
    } else {
      const payload: ApplicableCoursePayload[] = [
        {
          course_id: courseId,
          limit_type: null,
        },
      ];
      await courseOrderLimitService.deleteCourseOrderLimit(payload);
    }
  }
  function handleOpenSelectTargetMenu() {
    setShowSelectTargetMenu(true);
  }

  function handleCloseSelectTargetMenu() {
    setShowSelectTargetMenu(false);
  }

  function handleReflectSelectTargetMenu(selectedMenuCourses: MenuCourse[]) {
    setShowSelectTargetMenu(false);

    setMenuCourses(selectedMenuCourses);
    const menu_ids = selectedMenuCourses.map((menu) => menu.menu_id);
    setValue('menu_ids', menu_ids);
  }

  function handleOpenSelectConditionMenu() {
    setShowSelectConditionMenu(true);
  }

  function handleCloseSelectConditionMenu() {
    setShowSelectConditionMenu(false);
  }

  function handleReflectSelectConditionMenu(selectedMenuCourses: MenuCourse[]) {
    setShowSelectConditionMenu(false);

    setMenuCourseAdditionals(selectedMenuCourses);
    const menu_ids = selectedMenuCourses.map((menu) => menu.menu_id);
    setValue('menu_additional_ids', menu_ids);
  }

  function changeCourseClassification(e: ChangeEvent<HTMLSelectElement>) {
    const value = Number(e.target.value);
    if (!courseId) {
      if (value === COURSE_CLASSIFICATION_ALL_YOU_CAN_EAT) {
        // remove auto target_menus
        const autoAddMenuIds = autoAddMenuCourses.map((menu) => menu.menu_id);
        const newMenuCourseAdditionals = menuCourseAdditionals.filter(
          (menuCourse) => !autoAddMenuIds.includes(menuCourse.menu_id)
        );
        setMenuCourseAdditionals(newMenuCourseAdditionals);
        setValue(
          'menu_additional_ids',
          newMenuCourseAdditionals.map((menuCourse) => menuCourse.menu_id)
        );
      } else if (value === COURSE_CLASSIFICATION_ALL_YOU_CAN_DRINK) {
        // add auto target_menus
        const exsistMenuCourseAdditionalIds = menuCourseAdditionals.map((menuCourse) => menuCourse.menu_id);
        const newMenuCourseAdditionals = [...menuCourseAdditionals];
        autoAddMenuCourses.forEach((autoMenuCourse) => {
          if (!exsistMenuCourseAdditionalIds.includes(autoMenuCourse.menu_id)) {
            newMenuCourseAdditionals.push(autoMenuCourse);
          }
        });
        setMenuCourseAdditionals(newMenuCourseAdditionals);
        setValue(
          'menu_additional_ids',
          newMenuCourseAdditionals.map((menuCourse) => menuCourse.menu_id)
        );
      }
    }
  }

  if (isLoading) {
    return null;
  }

  const menuIds = menuCourses.map((menu) => menu.menu_id);
  const menuAdditionalIds = menuCourseAdditionals.map((menu) => menu.menu_id);
  // get list menu id with menuCourseAdditionals filtered by is_auto_add = True
  const menuIdsWithAutoAdd = menuCourseAdditionals.filter((menu) => menu.is_auto_add).map((menu) => menu.menu_id);

  return (
    <>
      <Modal show dialogClassName="course-detail-modal" backdrop="static" onHide={handleClose} centered>
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={`${menuBookName}｜${t('CourseDetailModal.head_title')}`} />
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex justify-content-between my-3">
              <div className="pe-3">
                <div>{t('CourseDetailModal.guide_title')}</div>
                <div className="ps-3">{t('CourseDetailModal.guide_description')}</div>
              </div>
              <div className="ps-3">
                <Button type="submit" className="btn btn--dark-green">
                  {t('CourseDetailModal.save')}
                </Button>
              </div>
            </div>

            <Table bordered className="form-course">
              <tbody>
                <tr>
                  <th colSpan={3}>{t('CourseDetailModal.course_name')}</th>
                  <td colSpan={3}>
                    <Form.Control className="text-center" {...register('name')}></Form.Control>
                    {errors.name && <small className="text-danger d-inline-block mt-1">{errors.name.message}</small>}
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('CourseDetailModal.classification')}</th>
                  <td colSpan={3}>
                    {classificationSource && (
                      <Form.Select
                        disabled={!courseId ? false : true}
                        className="text-center"
                        {...register('classification')}
                        onChange={changeCourseClassification}
                      >
                        {Object.keys(classificationSource).map(function (key) {
                          return (
                            <option key={key} value={key}>
                              {classificationSource[Number(key)]}
                            </option>
                          );
                        })}
                      </Form.Select>
                    )}
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('CourseDetailModal.priority_order')}</th>
                  <td colSpan={3}>
                    <span>{t('CourseDetailModal.priority_guide')}</span>
                    <Form.Control className="text-center" {...register('priority')} />
                    {errors.priority && (
                      <small className="text-danger d-inline-block mt-1">{errors.priority.message}</small>
                    )}
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('CourseDetailModal.limit_type')}</th>
                  <td colSpan={3}>
                    <span>{t('CourseDetailModal.limit_type_guide')}</span>
                    <div className="d-flex justify-content-center">
                      {limitTypeList && (
                        <Form.Select {...register('limit_type')}>
                          {limitTypeList.map((item, index) => (
                            <option className="text-center" key={index} value={item?.id}>
                              {item.name}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>
                    <span>{t('CourseDetailModal.target_menu')}</span>
                    <br />
                    <span>({t('CourseDetailModal.target_menu_description')})</span>
                  </th>
                  <td colSpan={3}>
                    <div className="d-flex w-100 h-100 flex-column">
                      <ul className="list-unstyled flex-fill">
                        {menuCourses.map((menu) => {
                          return (
                            <li key={menu.menu_id} className="small">
                              <span>
                                {menu.cd}
                                {COUPON_CLASSIFICATIONS[menu.coupon_classification] ?? ''}
                              </span>
                              &nbsp;
                              <span>
                                {menu.name}{' '}
                                {couponClassificationSource &&
                                  `(${couponClassificationSource[menu.coupon_classification] ?? ''})`}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="text-end">
                        <Button onClick={handleOpenSelectTargetMenu}>{t('App.change')}</Button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>
                    <span>{t('CourseDetailModal.condition')}</span>
                    <br />
                    <span>({t('CourseDetailModal.condition_description')})</span>
                    <br />
                    <br />
                    <span>({t('CourseDetailModal.condition_note')})</span>
                  </th>
                  <td colSpan={3}>
                    <div className="d-flex w-100 h-100 flex-column">
                      <ul className="list-unstyled flex-fill">
                        {menuCourseAdditionals.map(function (menu) {
                          return (
                            <li key={menu.menu_id} className="small">
                              <span>
                                {menu.cd}
                                {COUPON_CLASSIFICATIONS[menu.coupon_classification] ?? ''}
                              </span>
                              &nbsp;
                              <span>
                                {menu.name}{' '}
                                {couponClassificationSource &&
                                  `(${couponClassificationSource[menu.coupon_classification] ?? ''})`}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="text-end">
                        <Button onClick={handleOpenSelectConditionMenu}>{t('App.change')}</Button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('CourseDetailModal.limited_time')}</th>
                  <td colSpan={3}>
                    <Form.Control
                      className="text-center"
                      type="number"
                      maxLength={3}
                      onInput={(event: FormEvent) => {
                        // if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);
                        const inputElem = event.currentTarget as HTMLInputElement;

                        var regex = /[^0-9]/gi;
                        // Replace other characters that are not in regex pattern
                        inputElem.value = inputElem.value.replace(regex, '');

                        if (inputElem.value.length === 0) {
                          inputElem.value = '0';
                          return;
                        }

                        if (inputElem.value.length > inputElem.maxLength) {
                          inputElem.value = inputElem.value.slice(0, inputElem.maxLength);
                        }

                        inputElem.value = Number.parseInt(inputElem.value).toString();
                      }}
                      min={0}
                      max={999}
                      defaultValue={0}
                      {...register('time_limit_minutes')}
                    ></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th rowSpan={watchBackgroundFlg ? 9 : 2} style={{ width: '25%' }}>
                    {t('CourseDetailModal.button_heading_settings')}
                  </th>
                  <th colSpan={2} rowSpan={2}>
                    {t('CourseDetailModal.color_setting')}
                  </th>
                  <td rowSpan={2}>
                    <div className="text-center mb-2">{t('CourseDetailModal.color_setting_guide')}</div>
                    <div
                      style={{ background: watchBackgroundColor, color: watchTextColor, fontSize: '1rem' }}
                      className="text-center p-1"
                    >
                      {watchName}
                    </div>
                  </td>
                  <th style={{ width: '10%' }}>{t('CourseDetailModal.background')}</th>
                  <td width="10%">
                    <Button
                      className="d-inline-block"
                      onClick={handleBackgroundSettingClick}
                      style={{ minWidth: '2rem' }}
                    >
                      {t('App.change')}
                    </Button>
                    <Form.Control
                      ref={backgroundSettingInputRef}
                      type="color"
                      id="background-setting"
                      className="invisible"
                      style={{ height: 0, padding: 0 }}
                      defaultValue={getValues('background_color')}
                      title="Choose your color"
                      onChange={(event) => {
                        setValue('background_color', event.target.value);
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th>{t('CourseDetailModal.color')}</th>
                  <td>
                    <Button className="d-inline-block" onClick={handleColorSettingClick} style={{ minWidth: '2rem' }}>
                      {t('App.change')}
                    </Button>
                    <Form.Control
                      ref={colorSettingInputRef}
                      type="color"
                      id="color-setting"
                      className="invisible"
                      style={{ height: 0, padding: 0 }}
                      defaultValue={getValues('text_color')}
                      title="Choose your color"
                      onChange={(event) => {
                        setValue('text_color', event.target.value);
                      }}
                    />
                  </td>
                </tr>
                <tr className="d-none">
                  <th rowSpan={watchBackgroundFlg ? 7 : 1} style={{ width: '4%' }}>
                    {t('CourseDetailModal.for_tablet_order')}
                  </th>
                  <th style={{ width: '16%' }}>{t('CourseDetailModal.background_setting')}</th>
                  <td colSpan={3}>
                    <div>{t('CourseDetailModal.background_setting_guide')}</div>
                    <div className="d-flex justify-content-center">
                      <Form.Check
                        type="switch"
                        label=""
                        size={1}
                        checked={watchBackgroundFlg}
                        onChange={(event) => {
                          setValue('background_flg', event.target.checked);
                        }}
                      />
                    </div>
                  </td>
                </tr>
                <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                  <th>{t('CourseDetailModal.japanese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="background_img_ja"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                  <th>{t('CourseDetailModal.english')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="background_img_en"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                  <th>{t('CourseDetailModal.simplified_chinese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="background_img_zh_hans"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                  <th>{t('CourseDetailModal.transitional_chinese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="background_img_zh_hant"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                  <th>{t('CourseDetailModal.korean')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="background_img_ko"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                  <th>{t('CourseDetailModal.vietnamese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="background_img_vi"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr>
                  <th rowSpan={6} colSpan={2} style={{ width: '25%' }}>
                    {t('CourseDetailModal.display_name')}
                  </th>
                  <th style={{ width: '25%' }}>{t('CourseDetailModal.japanese')}</th>
                  <td colSpan={3} style={{ width: '50%' }}>
                    <Form.Control {...register('display_name_ja')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('CourseDetailModal.english')}</th>
                  <td colSpan={3}>
                    <Form.Control {...register('display_name_en')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('CourseDetailModal.simplified_chinese')}</th>
                  <td colSpan={3}>
                    <Form.Control {...register('display_name_zh_hans')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('CourseDetailModal.transitional_chinese')}</th>
                  <td colSpan={3}>
                    <Form.Control {...register('display_name_zh_hant')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('CourseDetailModal.korean')}</th>
                  <td colSpan={3}>
                    <Form.Control {...register('display_name_ko')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('CourseDetailModal.vietnamese')}</th>
                  <td colSpan={3}>
                    <Form.Control {...register('display_name_vi')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th rowSpan={watchIntroduceFlg ? 14 : 2}>
                    {t('MenuBookDetailModal.introduce_image')}
                    <br />
                    {t('MenuBookDetailModal.introduce_image_note')}
                  </th>
                </tr>
                <tr>
                  <th colSpan={2}>{t('CourseDetailModal.allow')}</th>
                  <td colSpan={3}>
                    <div className="h-100 d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        label=""
                        size={1}
                        checked={watchIntroduceFlg}
                        onChange={(event) => {
                          setValue('introduce_flg', event.target.checked);
                        }}
                      />
                    </div>
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th rowSpan={6}>{t('MenuBookDetailModal.for_mobile_order')}</th>
                  <th>{t('MenuBookDetailModal.japanese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="mobile_order_img_ja"
                      control={control}
                      minWidth={IMAGE_SIZES.course_mobile_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.course_mobile_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.course_mobile_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.course_mobile_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.course_mobile_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.english')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="mobile_order_img_en"
                      control={control}
                      minWidth={IMAGE_SIZES.course_mobile_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.course_mobile_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.course_mobile_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.course_mobile_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.course_mobile_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.simplified_chinese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="mobile_order_img_zh_hans"
                      control={control}
                      minWidth={IMAGE_SIZES.course_mobile_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.course_mobile_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.course_mobile_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.course_mobile_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.course_mobile_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.transitional_chinese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="mobile_order_img_zh_hant"
                      control={control}
                      minWidth={IMAGE_SIZES.course_mobile_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.course_mobile_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.course_mobile_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.course_mobile_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.course_mobile_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.korean')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="mobile_order_img_ko"
                      control={control}
                      minWidth={IMAGE_SIZES.course_mobile_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.course_mobile_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.course_mobile_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.course_mobile_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.course_mobile_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.vietnamese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="mobile_order_img_vi"
                      control={control}
                      minWidth={IMAGE_SIZES.course_mobile_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.course_mobile_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.course_mobile_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.course_mobile_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.course_mobile_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th rowSpan={6}>{t('MenuBookDetailModal.for_tablet_order')}</th>
                  <th>{t('MenuBookDetailModal.japanese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="tablet_order_img_ja"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.english')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="tablet_order_img_en"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.simplified_chinese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="tablet_order_img_zh_hans"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.transitional_chinese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="tablet_order_img_zh_hant"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.korean')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="tablet_order_img_ko"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr className={!watchIntroduceFlg ? 'd-none' : ''}>
                  <th>{t('MenuBookDetailModal.vietnamese')}</th>
                  <td colSpan={3}>
                    <ImageUploadControl
                      name="tablet_order_img_vi"
                      control={control}
                      minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                      minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                      maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                      maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                      maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                    />
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('CourseDetailModal.remark')}</th>
                  <td colSpan={3}>
                    <Form.Control as="textarea" rows={3} {...register('note')}></Form.Control>
                  </td>
                </tr>
              </tbody>
            </Table>
          </form>
        </Modal.Body>
      </Modal>

      {showSelectTargetMenu && couponClassificationSource && (
        <SelectCourseMenuModal
          selectedIds={menuIds}
          couponClassificationSource={couponClassificationSource}
          onClose={handleCloseSelectTargetMenu}
          onReflect={handleReflectSelectTargetMenu}
        />
      )}
      {showSelectConditionMenu && couponClassificationSource && (
        <SelectCourseMenuModal
          selectedIds={menuAdditionalIds}
          couponClassificationSource={couponClassificationSource}
          onClose={handleCloseSelectConditionMenu}
          onReflect={handleReflectSelectConditionMenu}
          disabledIds={menuIdsWithAutoAdd}
        />
      )}
    </>
  );
}

export default CourseDetailModal;
